"use client";

import { c as _c } from "react/compiler-runtime";
import { useEffect, useState } from "react";
import { reverseGeocode } from "~/lib/mapbox/mapbox";

/**
 * The driver's city, but only if the browser will say so without being asked.
 *
 * The events feed's only geographic bound is `city`, an exact string match on
 * the backend — so this exists solely to fill that parameter. Every failure
 * path resolves to `null`, which means "unscoped list", never an error: a
 * landing page must not surface a geolocation problem as a broken section.
 *
 * It deliberately never PROMPTS. `navigator.permissions` is consulted first and
 * the position is only requested when permission is already `granted`. A
 * location prompt on first paint of a marketing page is the kind of thing
 * people dismiss reflexively — and once dismissed, it is dismissed for good.
 * The mobile app takes the same position (`getLocationIfGranted`); a driver who
 * wants city-scoped results grants location somewhere it is actually explained.
 *
 * `null` covers both "still deciding" and "there is no city", and callers do not
 * need to tell those apart: the only consumer uses a city to *refine* a list it
 * already has, so no city simply means no refinement — the same correct
 * behaviour in both cases.
 */
export function useNearbyCity() {
  const $ = _c(4);
  const [city, setCity] = useState(null);
  let t0;
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = () => {
      let cancelled = false;
      const finish = value => {
        if (cancelled) {
          return;
        }
        setCity(value);
      };
      const resolve_0 = async function resolve() {
        if (typeof navigator === "undefined" || !navigator.geolocation) {
          finish(null);
          return;
        }
        if (!navigator.permissions?.query) {
          finish(null);
          return;
        }
        try {
          const status = await navigator.permissions.query({
            name: "geolocation"
          });
          if (status.state !== "granted") {
            finish(null);
            return;
          }
        } catch {
          finish(null);
          return;
        }
        const coords = await new Promise(_temp);
        if (!coords) {
          finish(null);
          return;
        }
        const address = await reverseGeocode({
          lat: coords.latitude,
          lng: coords.longitude
        });
        const resolved = address?.city?.trim();
        finish(resolved ? resolved : null);
      };
      resolve_0();
      return () => {
        cancelled = true;
      };
    };
    t1 = [];
    $[0] = t0;
    $[1] = t1;
  } else {
    t0 = $[0];
    t1 = $[1];
  }
  useEffect(t0, t1);
  let t2;
  if ($[2] !== city) {
    t2 = {
      city
    };
    $[2] = city;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  return t2;
}
function _temp(resolve) {
  navigator.geolocation.getCurrentPosition(position => resolve(position.coords), () => resolve(null), {
    maximumAge: 900000,
    timeout: 8000
  });
}